import { QueryClient } from "@tanstack/react-query";
import { apiUrl } from "../config/api";
import { authService } from "./firebase-auth-service";

const absoluteUrlPattern = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;

const resolveUrl = (url: unknown) => {
  if (typeof url !== "string") {
    return url as string;
  }

  if (!url || absoluteUrlPattern.test(url) || url.startsWith("data:") || url.startsWith("blob:")) {
    return url;
  }

  return apiUrl(url);
};

type FetcherOptions = {
  on401?: "throw" | "returnNull";
};

type ApiRequestMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export const getQueryFn = (options?: FetcherOptions) => {
  return async ({ queryKey }: { queryKey: any }) => {
    const [url] = queryKey;
    const targetUrl = resolveUrl(url);
    console.log(`Fetching data from: ${targetUrl}`);
    
    try {
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };

      // Try to get Firebase auth token
      try {
        const token = await authService.getIdToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log(`Token added to request for ${targetUrl}`);
        }
        if (!headers['Authorization']) {
          console.log('No token after first attempt, forcing auth refresh...');
          await authService.refreshAuthState();
          const refreshedToken = await authService.getIdToken(true);
          if (refreshedToken) {
            headers['Authorization'] = `Bearer ${refreshedToken}`;
            console.log(`Token added on retry for ${targetUrl}`);
          }
        }
      } catch (authError) {
        console.log(`No valid token available for ${targetUrl}, proceeding without auth`);
        // Continue without token - let the backend handle authentication
      }
      
      const res = await fetch(targetUrl, {
        headers
      });

      if (res.status === 401 && options?.on401 === "returnNull") {
        return null;
      }

      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Error fetching ${targetUrl}: ${errorText || res.statusText}`);
        throw new Error(errorText || res.statusText);
      }

      // For empty responses like 204 No Content
      if (res.status === 204) {
        return null;
      }

      const data = await res.json();
      console.log(`Data received from ${targetUrl}:`, data);
      return data;
    } catch (error) {
      console.error(`Failed to fetch from ${targetUrl}:`, error);
      console.error(`Error details:`, error.message, error.stack);
      throw error;
    }
  };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn(),
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error: any) => {
        // Don't retry on 401, 403, 404, or 500 errors
        if (error?.status === 401 || error?.status === 403 || error?.status === 404 || error?.status === 500) {
          return false;
        }
        // For other errors, only retry once (reduced from 3)
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: false, // Changed from true to prevent refetch storms
      refetchInterval: false, // Explicitly disable automatic refetching
    },
  },
});

export const apiRequest = async (
  methodOrUrl: ApiRequestMethod | string,
  urlOrMethod: string | ApiRequestMethod,
  data?: any
) => {
  // Determine which parameter is the method and which is the URL
  let method: string;
  let url: string;

  if (['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(methodOrUrl as string)) {
    // First parameter is the method (e.g., "POST", "/api/auth/login", data)
    method = methodOrUrl as string;
    url = urlOrMethod as string;
  } else {
    // First parameter is the URL (e.g., "/api/vidvault", "POST", data)
    url = methodOrUrl as string;
    method = urlOrMethod as string;
  }

  const targetUrl = resolveUrl(url);

  console.log(`🔄 [apiRequest] Making ${method} request to ${targetUrl}`, data ? 'with data' : 'without data');

  try {
    // Get Firebase auth token with retry mechanism and initialization wait
    let token = null;
    console.log('🔑 [apiRequest] Starting token retrieval...');

    // CRITICAL: Check for signed-out flag first
    const signedOutFlag = localStorage.getItem('yapzap_signed_out');
    if (signedOutFlag) {
      console.log('🔑 [apiRequest] User is signed out - skipping token retrieval');
    } else {
      try {
        // First, ensure Firebase auth is initialized by getting the auth instance
        const auth = await authService.getAuth();
        console.log('🔑 [apiRequest] Auth instance ready, currentUser:', auth.currentUser?.email || 'No currentUser');

        // If we have a current user directly on auth, get their token
        if (auth.currentUser) {
          console.log('🔑 [apiRequest] Getting token directly from auth.currentUser...');
          token = await auth.currentUser.getIdToken();
          console.log('🔑 [apiRequest] Got token from auth.currentUser:', token ? `Token length ${token.length}` : 'No token');
        }

        // Fallback to authService methods if direct approach didn't work
        if (!token) {
          console.log('🔑 [apiRequest] Falling back to authService.getIdToken()...');
          token = await authService.getIdToken();
          console.log('🔑 [apiRequest] authService.getIdToken result:', token ? `Token length ${token.length}` : 'No token');
        }

        if (!token) {
          // Force refresh token if initial call returns null
          console.log('🔑 [apiRequest] No token from getIdToken, trying getCurrentUser...');
          const currentUser = await authService.getCurrentUser();
          console.log('🔑 [apiRequest] getCurrentUser result:', currentUser ? currentUser.email : 'No user');
          if (currentUser) {
            console.log('🔑 [apiRequest] Calling currentUser.getIdToken(true)...');
            token = await currentUser.getIdToken(true); // Force refresh
            console.log('🔑 [apiRequest] Token from currentUser:', token ? `Token length ${token.length}` : 'No token');
          }
        }
        if (!token) {
          console.log('🔑 [apiRequest] Still no token, attempting auth refresh...');
          await authService.refreshAuthState();
          token = await authService.getIdToken(true);
          console.log('🔑 [apiRequest] Token after auth refresh:', token ? `Token length ${token.length}` : 'No token');
        }
      } catch (authError) {
        console.error('🔑 [apiRequest] Error getting Firebase token:', authError);
      }
    }

    console.log('🔑 [apiRequest] Final token for request:', token ? `Token length ${token.length}` : 'NO TOKEN - REQUEST MAY FAIL IF AUTH REQUIRED');
    
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    };
    
    // Add Authorization header if token exists
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
      console.log('Authorization header added to request');
    } else {
      console.warn('No Firebase token available for request - user may not be authenticated');
    }
    
    const options: RequestInit = {
      method,
      headers,
      cache: "no-cache",
    };

    if (data) {
      if (data instanceof FormData) {
        // For FormData, don't set Content-Type header - let browser set it with boundary
        delete headers['Content-Type'];
        options.body = data;
      } else {
        options.body = JSON.stringify(data);
      }
    }

    const response = await fetch(targetUrl, options);
    
    // Debug log for response status and headers
    console.log(`Response from ${targetUrl}: status=${response.status}, ok=${response.ok}`);
    
    // Special handling for membership/join operations
    const isJoinOperation = targetUrl.includes('/join');
    const isMembershipCheck = targetUrl.includes('/is-member');
    
    if ((isJoinOperation || isMembershipCheck) && response.status === 401) {
      console.error('Authentication error during membership operation!');
      // We could redirect to login here if needed
    }

    if (!response.ok) {
      let errorMessage = response.statusText;
      
      // Try to parse error message from response
      let errorDetails = null;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
        errorDetails = errorData.details || errorData.message;
        console.error(`Error response from ${targetUrl}:`, errorData);
        
        // Check for auth errors
        if (response.status === 401) {
          console.error('Authentication error detected. Session may be invalid.');
        }
        
      } catch (e) {
        // If response is not JSON, try to get text
        try {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
          console.error(`Error text from ${targetUrl}:`, errorText);
        } catch (e2) {
          console.error(`Could not parse error response from ${targetUrl}`);
        }
      }
      
      // Create a more detailed error object
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).response = response;
      (error as any).details = errorDetails;
      throw error;
    }

    // For methods that don't return data (like DELETE)
    if (response.status === 204) {
      return { ok: true, status: response.status, statusText: response.statusText };
    }
    
    // Check if there's any content to parse
    const contentType = response.headers.get('content-type');
    
    // Try to parse as JSON regardless of content-type header (some APIs don't set it correctly)
    try {
      const data = await response.json();
      console.log(`Parsed JSON data from ${targetUrl}:`, data);
      return data;
    } catch (jsonError) {
      console.log(`Could not parse JSON from ${targetUrl}, content-type: ${contentType}`);
      // Return the raw response for non-JSON responses
      return { ok: true, status: response.status, statusText: response.statusText, response: {} };
    }
  } catch (error) {
    console.error(`Error during ${method} request to ${targetUrl}:`, error);
    throw error;
  }
};
